// 商户门户认证 API（镜像四门户契约）
// login → 并行拉 me+menus 写 session；me.merchantId 写入 session.merchantId
//
// FE-E2b 薄壳化：五件套模板（login/logout/refresh/me/menus）下沉
// @shared/api/auth 工厂；本文件保留端配置、代登录对与类型，导出面不变。
import {
  merchantGet,
  merchantPost,
  clearSession,
  DEFAULT_MERCHANT_ID,
  loadSession,
  saveSession,
  signOutLocal,
  USE_MOCK,
  type ApiResult,
  type MerchantSession,
  type MenuNodeView,
  type PortalSessionView,
} from './client';
import { createAuthApi, type TokenPair } from '@shared/api/auth';
import { sessionMerchantIdFromMe, toMerchantIdString } from './authCore.ts';

export type { TokenPair, MenuNodeView };

const auth = createAuthApi<MerchantSession, PortalSessionView>({
  get: merchantGet,
  post: merchantPost,
  loadSession,
  saveSession,
  clearSession,
  signOutLocal,
  mockMe: () => {
    const s = loadSession();
    // subjectId 是会话主体数值 ID（mock 常量），不得由 19 位 merchantId 经 Number() 派生
    const merchantId = toMerchantIdString(s?.merchantId) ?? String(DEFAULT_MERCHANT_ID);
    return {
      subjectId: DEFAULT_MERCHANT_ID,
      channel: 'MERCHANT',
      principal: s?.principal || 'merchant@mock',
      roles: [],
      permissions: [],
      merchantId,
      mustChangePassword: s?.me?.mustChangePassword === true,
      mfaSetupRequired: s?.me?.mfaSetupRequired === true,
      otpGraceRemainingSeconds: s?.me?.otpGraceRemainingSeconds ?? 0,
    };
  },
  buildSession: (t: TokenPair, principal: string): MerchantSession => ({
    accessToken: t.accessToken,
    refreshToken: t.refreshToken,
    principal,
    expiresAt: t.expiresIn ? Date.now() + t.expiresIn * 1000 : undefined,
  }),
  // 作用域商户号来自 me.merchantId（AC-3 fail-close：me 失败/缺字段不写默认 20）
  mergeAfterLogin: (current, meRes, menusRes) => {
    const merchantId = meRes.ok ? sessionMerchantIdFromMe(meRes.data) : null;
    return {
      ...current,
      merchantId: merchantId ?? undefined,
      me: meRes.ok ? meRes.data : null,
      menus: menusRes.ok ? menusRes.data : null,
    };
  },
  // refreshSessionMe 同步回写作用域商户号（同样只认 me.merchantId）
  applyMe: (current, me) => ({
    ...current,
    me,
    merchantId: sessionMerchantIdFromMe(me) ?? undefined,
  }),
});

export const { fetchMe, refreshSessionMe, fetchMenus, refreshTokens, logout, login, logoutLocal } = auth;

/**
 * 代登录换会话响应（契约 §0.3；刻意无 refreshToken——代登录会话不可续期 D3）。
 * 后端 `MerchantPortalJacksonConfig` 把 Long 出站为 string（19 位雪花 ID 超 JS MAX_SAFE_INTEGER），
 * 前端声明同步为 string，全程原样透传。
 */
export type PortalLoginResponse = {
  accessToken: string;
  tokenType?: string;
  expiresIn?: number;
  impersonated: boolean;
  principal?: string;
  subjectId?: number;
  merchantId?: string;
};

/**
 * 运营代登录票据消费（POST /auth/portal-login，permitAll，skipAuth）。
 * 错误码 3428 / 3429 / 3430 / USER_DISABLED 由落地页分流文案，不走全局错误处理。
 */
export async function portalLogin(ticket: string): Promise<ApiResult<PortalLoginResponse>> {
  return merchantPost<PortalLoginResponse>(
    '/auth/portal-login',
    { ticket },
    () => ({
      accessToken: 'mock-access-portal-login',
      tokenType: 'Bearer',
      expiresIn: 3600,
      impersonated: true,
      principal: 'merchant@mock',
      subjectId: DEFAULT_MERCHANT_ID,
      merchantId: String(DEFAULT_MERCHANT_ID),
    }),
    { skipAuth: true },
  );
}

export { USE_MOCK };
